import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getCountFromServer,
} from "firebase/firestore";
import { auth, db } from "../../../fb";
import { orderStatus, permissions, taskStatus } from "@/constants";

const defaultState = {
  counters: {
    shopifyTasks: 0,
    DRAW: 0,
    TEMPLATING: 0,
    REVIEW: 0,
    REFINEMENT: 0,
    ordersToFulfill: 0,
    manualFulfillments: 0,
    support: 0,
    ratings: 0,
  },
  unsubRatings: () => {},
};

const getters = {
  counters: (state) => state.counters,
};

const mutations = {
  setCounter: (state, { key, value }) => {
    state.counters = { ...state.counters, [key]: value };
  },
};

const safeCount = async (q) => {
  try {
    const snap = await getCountFromServer(q);
    return snap.data().count;
  } catch (e) {
    console.error("counter query failed", e);
    return 0;
  }
};

const tasksQueryForStatus = (status, getters) => {
  const coll = collection(db, "tasks");
  const isAdmin =
    getters.userPermissions.includes(permissions.ADMIN) ||
    getters.userPermissions.includes(permissions.MANAGE_TASKS);

  if (isAdmin) {
    return query(coll, where("status", "==", status));
  }

  const managedUsers = getters.managedUsers || [];
  const showTaskMarket = getters.userPermissions.includes(
    permissions.TASKMARKET
  );
  const assigneeFilter = [
    ...(showTaskMarket ? [""] : []),
    auth.currentUser?.uid?.toString(),
    ...managedUsers,
  ].filter(Boolean);

  if (status === taskStatus.REVIEW) {
    return query(
      coll,
      where("status", "==", status),
      where("assignedTo", "in", managedUsers.length ? managedUsers : [""])
    );
  }

  if (!assigneeFilter.length) {
    return null;
  }

  return query(
    coll,
    where("status", "==", status),
    where("assignedTo", "in", assigneeFilter)
  );
};

const actions = {
  async loadCounters({ commit, getters }) {
    const userPermissions = getters.userPermissions || [];
    const has = (p) => userPermissions.includes(p);
    const isAdmin = has(permissions.ADMIN);

    const jobs = [];

    const setCount = (key, value) => commit("setCounter", { key, value });

    if (has(permissions.TASK_IMPORTING) || isAdmin) {
      // Mirror the actual subscribe query so the count matches what is rendered.
      // orderBy("createdAt") drops docs missing that field, otherwise the
      // pre-load count is higher than the post-load list length.
      jobs.push(
        safeCount(
          query(
            collection(db, "shopifyTasks"),
            orderBy("createdAt"),
            limit(10000)
          )
        ).then((c) => setCount("shopifyTasks", c))
      );
    }

    const taskCounterPermissions = [
      { perm: permissions.TASK_DRAWING, status: taskStatus.DRAW },
      { perm: permissions.TASK_TEMPLATING, status: taskStatus.TEMPLATING },
      { perm: permissions.TASK_REFINEMENT, status: taskStatus.REFINEMENT },
      { perm: permissions.MANAGER, status: taskStatus.REVIEW },
    ];

    taskCounterPermissions.forEach(({ perm, status }) => {
      if (has(perm) || isAdmin) {
        const q = tasksQueryForStatus(status, getters);
        if (q) {
          jobs.push(safeCount(q).then((c) => setCount(status, c)));
        }
      }
    });

    if (has(permissions.FULFILL_ORDERS) || isAdmin) {
      const ordersColl = collection(db, "orders");
      jobs.push(
        safeCount(
          query(ordersColl, where("status", "==", orderStatus.READY_FOR_FULFILL))
        ).then((c) => setCount("ordersToFulfill", c))
      );
      jobs.push(
        safeCount(collection(db, "manualFulfillments")).then((c) =>
          setCount("manualFulfillments", c)
        )
      );
    }

    if (has(permissions.MANAGE_TASKS) || isAdmin) {
      const supportColl = collection(db, "support");
      jobs.push(
        safeCount(query(supportColl, where("pending", "==", true))).then((c) =>
          setCount("support", c)
        )
      );
    }

    await Promise.all(jobs);

    // Ratings count is kept live via its own listener so the badge updates
    // when new negative ratings come in.
    setCount("ratings", 0);
    // dispatch handled below; not awaited because it's an open subscription
  },
  subscribeRatingsCounter({ commit, getters, state }) {
    // Idempotent
    if (state.unsubRatings && state.unsubRatings.__active) return;

    const userPermissions = getters.userPermissions || [];
    const has = (p) => userPermissions.includes(p);
    const isAdmin = has(permissions.ADMIN);

    let q;
    if (has(permissions.MANAGE_RATINGS) || isAdmin) {
      // Tasks with negative feedback. limit matches AllRatingsAdmin's bad query.
      q = query(
        collection(db, "tasks"),
        where("feedback.satisfied", "==", false),
        limit(100)
      );
    } else if (has(permissions.TASK_DRAWING) && auth.currentUser?.uid) {
      // Artists see ratings the admin shared with them.
      q = query(
        collection(db, "ratings"),
        where("artistId", "==", auth.currentUser.uid),
        limit(100)
      );
    } else {
      return;
    }

    if (typeof state.unsubRatings === "function") state.unsubRatings();
    const unsub = onSnapshot(q, (snapshot) => {
      const negatives = snapshot.docs.filter((d) => {
        const data = d.data();
        // Tasks store satisfied under feedback.satisfied; ratings docs at top level.
        const satisfied = data?.feedback?.satisfied ?? data?.satisfied;
        return satisfied === false;
      }).length;
      commit("setCounter", { key: "ratings", value: negatives });
    });
    unsub.__active = true;
    state.unsubRatings = unsub;
  },
  unsubRatingsCounter({ state }) {
    if (typeof state.unsubRatings === "function") state.unsubRatings();
    state.unsubRatings = () => {};
  },
};

const state = window.sessionStorage["taskDash"]
  ? JSON.parse(window.sessionStorage["taskDash"]).counters || { ...defaultState }
  : { ...defaultState };

if (!state.counters) {
  state.counters = { ...defaultState.counters };
}

export default {
  state,
  getters,
  actions,
  mutations,
};
